import React, { useState, useEffect } from 'react';
import { get, post } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';
import './Rewards.css';

const Rewards = () => {
  const [rewards, setRewards] = useState([]);
  const [users, setUsers] = useState([]);
  const [menuCategories, setMenuCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantError, setGrantError] = useState(null);
  const [grantSuccess, setGrantSuccess] = useState(null);

  // Grant form state
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [isComplimentary, setIsComplimentary] = useState(true);
  const [discountPercent, setDiscountPercent] = useState(100);
  const [expiresAt, setExpiresAt] = useState('');
  const [note, setNote] = useState('');

  const { getAuthToken } = useAuth();

  useEffect(() => {
    fetchRewards();
    fetchUsers();
    fetchMenu();
  }, [statusFilter]);

  const fetchRewards = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/rewards${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setRewards(data.rewards || []);
    } catch (err) {
      console.error('Error fetching rewards:', err);
      setError(err.message || 'Failed to load rewards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/users',
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setUsers(data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchMenu = async () => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: '/v1/menu',
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      setMenuCategories(data.categories || []);
    } catch (err) {
      console.error('Error fetching menu:', err);
    }
  };

  const openGrantModal = () => {
    setSelectedUser('');
    setSelectedMenuItem(null);
    setIsComplimentary(true);
    setDiscountPercent(100);
    setExpiresAt('');
    setNote('');
    setGrantError(null);
    setGrantSuccess(null);
    setShowGrantModal(true);
  };

  const closeGrantModal = () => {
    setShowGrantModal(false);
  };

  const handleMenuItemChange = (e) => {
    const itemId = e.target.value;
    if (!itemId) {
      setSelectedMenuItem(null);
      return;
    }

    // Find the item in menu categories
    for (const category of menuCategories) {
      const item = category.items?.find(i => i.item_id === itemId);
      if (item) {
        setSelectedMenuItem({
          ...item,
          category_name: category.name,
        });
        return;
      }
    }
  };

  const handleGrantReward = async () => {
    if (!selectedUser || !selectedMenuItem) {
      setGrantError('Please select a user and menu item');
      return;
    }

    try {
      setGrantLoading(true);
      setGrantError(null);
      setGrantSuccess(null);

      const token = await getAuthToken();
      if (!token) {
        setGrantError('Authentication token not found. Please log in again.');
        return;
      }

      const body = {
        user_id: selectedUser,
        menu_item_id: selectedMenuItem.item_id,
        menu_item_name: selectedMenuItem.name,
        menu_item_category: selectedMenuItem.category_name || 'Food',
        discount_percent: isComplimentary ? 100 : discountPercent,
      };

      if (selectedMenuItem.price) {
        body.original_price = selectedMenuItem.price;
      }

      if (expiresAt) {
        body.expires_at = expiresAt;
      }

      if (note) {
        body.note = note;
      }

      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/rewards/grant',
        options: {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          },
          body,
        },
      });

      await restOperation.response;

      const successMsg = selectedUser === 'EVERYONE'
        ? 'Reward granted to all users!'
        : 'Reward granted successfully!';
      setGrantSuccess(successMsg);

      // Refresh rewards list
      setTimeout(() => {
        fetchRewards();
        closeGrantModal();
      }, 1500);

    } catch (err) {
      console.error('Error granting reward:', err);
      setGrantError(err.message || 'Failed to grant reward. Please try again.');
    } finally {
      setGrantLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'status-active';
      case 'redeemed':
        return 'status-redeemed';
      case 'expired':
        return 'status-expired';
      default:
        return 'status-unknown';
    }
  };

  const getDiscountDisplay = (discount) => {
    if (discount === 100) {
      return 'FREE';
    }
    return `${discount}% OFF`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const getUserDisplay = (userId) => {
    if (userId === 'EVERYONE') {
      return 'All Users';
    }
    const user = users.find(u => u.sub === userId);
    if (user) {
      return user.nickname || user.phone || userId.substring(0, 8) + '...';
    }
    return userId.substring(0, 8) + '...';
  };

  return (
    <div className="rewards-container">
      <div className="page-header-section">
        <h2>Rewards Management</h2>
        <p>Grant and manage customer rewards</p>
      </div>

      <div className="content-header">
        <div className="header-left">
          <h3>Granted Rewards</h3>
          <div className="filter-group">
            <label>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="redeemed">Redeemed</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>
        <div className="header-actions">
          <button onClick={fetchRewards} className="refresh-button" disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button onClick={openGrantModal} className="grant-button">
            + Grant Reward
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading && !error ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading rewards...</p>
        </div>
      ) : rewards.length === 0 ? (
        <div className="empty-state">
          <p>No rewards found</p>
          <button onClick={openGrantModal} className="grant-button-large">
            Grant First Reward
          </button>
        </div>
      ) : (
        <div className="rewards-table-container">
          <table className="rewards-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Item</th>
                <th>Discount</th>
                <th>Status</th>
                <th>Granted</th>
                <th>Expires</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {rewards.map((reward, index) => (
                <tr key={reward.reward_id || index}>
                  <td className={`user-cell ${reward.user_id === 'EVERYONE' ? 'everyone' : ''}`}>
                    {reward.user_id === 'EVERYONE' ? (
                      <span className="everyone-badge">All Users</span>
                    ) : (
                      getUserDisplay(reward.user_id)
                    )}
                  </td>
                  <td className="item-cell">
                    <span className="item-name">{reward.menu_item_name}</span>
                    <span className="item-category">{reward.menu_item_category}</span>
                  </td>
                  <td>
                    <span className={`discount-badge ${reward.discount_percent === 100 ? 'free' : 'discounted'}`}>
                      {getDiscountDisplay(reward.discount_percent)}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(reward.status)}`}>
                      {reward.status}
                    </span>
                  </td>
                  <td className="date-cell">{formatDate(reward.granted_at)}</td>
                  <td className="date-cell">{formatDate(reward.expires_at)}</td>
                  <td className="note-cell">{reward.note || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rewards-count">
        Total Rewards: <strong>{rewards.length}</strong>
      </div>

      {/* Grant Reward Modal */}
      {showGrantModal && (
        <div className="modal-overlay" onClick={closeGrantModal}>
          <div className="modal-content grant-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Grant Reward</h3>
              <button className="close-button" onClick={closeGrantModal}>x</button>
            </div>

            <div className="modal-body">
              {grantError && (
                <div className="error-banner">
                  {grantError}
                </div>
              )}

              {grantSuccess && (
                <div className="success-banner">
                  {grantSuccess}
                </div>
              )}

              <div className="form-group">
                <label>Select Recipient *</label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  disabled={grantLoading}
                  className="form-select"
                >
                  <option value="">Choose recipient...</option>
                  <option value="EVERYONE" className="everyone-option">All Users</option>
                  <optgroup label="Individual Users">
                    {users.map((user) => (
                      <option key={user.sub} value={user.sub}>
                        {user.nickname || user.phone || user.username}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>

              <div className="form-group">
                <label>Select Menu Item *</label>
                <select
                  value={selectedMenuItem?.item_id || ''}
                  onChange={handleMenuItemChange}
                  disabled={grantLoading}
                  className="form-select"
                >
                  <option value="">Choose a menu item...</option>
                  {menuCategories.map((category) => (
                    <optgroup key={category.category_id} label={category.name}>
                      {category.items?.map((item) => (
                        <option key={item.item_id} value={item.item_id}>
                          {item.name} {item.price ? `($${item.price})` : ''}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isComplimentary}
                    onChange={(e) => {
                      setIsComplimentary(e.target.checked);
                      if (e.target.checked) {
                        setDiscountPercent(100);
                      }
                    }}
                    disabled={grantLoading}
                  />
                  <span className="checkbox-text">Complimentary (100% Free)</span>
                </label>
              </div>

              {!isComplimentary && (
                <div className="form-group">
                  <label>Discount Percentage</label>
                  <div className="discount-input-group">
                    <input
                      type="range"
                      min="5"
                      max="95"
                      step="5"
                      value={discountPercent}
                      onChange={(e) => setDiscountPercent(parseInt(e.target.value))}
                      disabled={grantLoading}
                      className="discount-slider"
                    />
                    <span className="discount-value">{discountPercent}% OFF</span>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Expiration Date (Optional)</label>
                <input
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  disabled={grantLoading}
                  min={new Date().toISOString().split('T')[0]}
                />
                <small className="help-text">Leave empty for no expiration</small>
              </div>

              <div className="form-group">
                <label>Note (Optional)</label>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={grantLoading}
                  placeholder="e.g., Birthday reward, VIP special"
                  maxLength={100}
                />
              </div>

              <div className="modal-actions">
                <button
                  className="cancel-button"
                  onClick={closeGrantModal}
                  disabled={grantLoading}
                >
                  Cancel
                </button>
                <button
                  className="submit-button grant-submit"
                  onClick={handleGrantReward}
                  disabled={grantLoading || !selectedUser || !selectedMenuItem}
                >
                  {grantLoading ? 'Granting...' : 'Grant Reward'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rewards;
